<template>
    <page-card :header="$t('pages.password_change.title', {account:rzaccount})">

        <!-- place for notifications -->
        <generic-notification :notifications.sync="notifications"></generic-notification>


        <dl class="row mt-1 mb-2" id="expirationPolicy">
            <dt class="col-12 ">{{ $t('pages.password_change.policy_name') }}</dt>
            <dd class="col-12 "> {{ expiration_policy.policy }}</dd>

            <dt class="col-12 ">{{ $t('pages.password_change.policy_max') }}</dt>
            <dd class="col-12 ">{{ expiration_policy.days }} {{ $t('pages.password_change.policy_days') }}</dd>
        </dl>

        <!--main form-->
        <b-form @submit.prevent="" v-if="!changeWasSuccessful">

            <!-- change username -->
            <b-form-group v-if="modeReset" :label="$t('pages.password_change.username')"
                          label-for="frmPasswordChangeUsername">
                <b-form-input type="text"
                              id="frmPasswordChangeUsername"
                              :state="usernameValid"
                              :placeholder="$t('pages.password_change.username')" v-model="username">
                </b-form-input>
            </b-form-group>

            <!-- current password -->
            <b-form-group :label="this.mode === 'change' ? $t('pages.password_change.mode_change') :
                                  this.mode === 'reset' ? $t('pages.password_change.mode_reset') : ''"
                          label-for="frmPasswordChangeCurrent">
                <b-form-input :type="passwordFieldType" autofocus
                              :state="currentPasswordProbablyOkay"
                              id="frmPasswordChangeCurrent"
                              :placeholder="this.mode === 'reset' ?
                              $t('pages.password_change.mode_reset_placeholder') :
                              $t('pages.password_change.mode_change_placeholder')" v-model="passwordCurrent">
                </b-form-input>
            </b-form-group>

            <!-- new password -->
            <b-form-group :label="$t('pages.password_change.new_password', {count: minPasswordLengthNew})"
                          label-for="frmPasswordChangeNew"
                          :state="newPasswordOkay">
                <span class="sr-only" id="frmPasswordChangeNewDescription">
                    {{ $t('pages.password_change.sr_only.trigger_request') }}
                </span>
                <b-input-group>
                    <b-form-input :type="passwordFieldType"
                                  :state="newPasswordOkay"
                                  debounce="1000"
                                  id="frmPasswordChangeNew"
                                  :placeholder="$t('pages.password_change.new_password_placeholder')" v-model="password">
                    </b-form-input>
                </b-input-group>

                <callout v-if="newPasswordLongEnough && passwordReused" callout-class="callout-danger">
                    {{ $t('pages.password_change.password_reused') }}
                </callout>

                <callout v-if="newPasswordEntered && !newPasswordLongEnough" callout-class="callout-danger">
                    {{ $t('pages.password_change.not_long_enough', {count: minPasswordLengthNew}) }}
                </callout>

                <!-- assessment callout -->
                <callout v-if="newPasswordEntered && newPasswordLongEnough && assessmentAvailable && !passwordReused" no-icon
                         :callout-class="moreThanZeroDays ? 'callout-success' : 'callout-danger'">

                    <!-- progress bar, displayed if password is somewhat good -->
                    <div v-if="moreThanZeroDays" class="mb-2">
                        <b-progress :value="effectiveValidity" :max="expiration_policy.days" animated aria-hidden="true"/>
                        <p class="mt-2">
                            {{
                                $t('pages.password_change.validity', {
                                    validity: effectiveValidity,
                                    max_validity: expiration_policy.days
                                })
                            }}
                        </p>
                    </div>

                    <!-- feedback, always displayed -->
                    <div class="mb-2">
                        <!-- password is bad -->
                        <p v-if="!moreThanZeroDays">
                            <b-icon-hand-thumbs-down variant="danger"/>
                            {{ $t('pages.password_change.password_really_bad') }}
                        </p>
                        <!-- password is secure enough -->
                        <p v-else>
                            <b-icon-hand-thumbs-up full variant="success"/>
                            {{ effectiveValidity === expiration_policy.days
                                ? $t('pages.password_change.password_very_good')
                                : $t('pages.password_change.password_good_enough')
                            }}
                        </p>
                    </div>

                    <!-- display hints, if available -->
                    <div v-if="hintsAvailable">
                        <b-list-group>
                            <b-list-group-item v-for="hint in hints" :key="hint">
                                <b-icon-arrow-right/>
                                {{ hint }}
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </callout>
            </b-form-group>

            <!-- password confirmation -->
            <b-form-group :label="$t('pages.password_change.new_confirm')"
                          label-for="frmPasswordChangeNew_confirmation">
                <b-form-input :type="passwordFieldType"
                              :state="newPasswordConfirmationOkay"
                              id="frmPasswordChangeNew_confirmation"
                              :placeholder="$t('pages.password_change.new_confirm_placeholder')" v-model='passwordConfirmation'>
                </b-form-input>
                <callout v-if="confirmationEntered && !passwordsMatch" callout-class="callout-danger">
                    {{ $t('pages.password_change.no_match') }}
                </callout>
            </b-form-group>

            <b-button-group class="d-flex">
                <!--password visibility button-->
                <b-button variant="primary"
                          class="w-100"
                          :pressed.sync="passwordsVisible"
                          aria-labelledby="toggleVisibilityDescription">
                    <b-icon-eye-slash v-if="passwordsVisible" aria-hidden="true"/>
                    <b-icon-eye v-else aria-hidden="true"/>
                    <span id="toggleVisibilityDescription">
                       <template v-if="passwordsVisible">
                           {{ $t('pages.password_change.button_hide') }}
                       </template>
                       <template v-else>
                           {{ $t('pages.password_change.button_show') }}
                       </template>
                   </span>
                </b-button>

                <!-- submit button -->
                <b-button variant="primary"
                          class="w-100"
                          id="passwordChangeSubmit"
                          v-on:click="changePassword"
                          :disabled="!isSendable || changeInProgress"
                          :aria-disabled="!isSendable">
                    <b-spinner v-if="changeInProgress" small aria-hidden="true"/>
                    <b-icon-upload v-else aria-hidden="true"/>
                    {{ $t('pages.password_change.button_submit') }}
                </b-button>
            </b-button-group>
        </b-form>

        <callout callout-class="callout-success" icon="check" v-if="changeWasSuccessful">
            <p class="font-weight-bold">{{ $t('pages.password_change.success') }}</p>
            <p>{{
                    $t('pages.password_change.validity', {
                        validity: effectiveValidity,
                        max_validity: expiration_policy.days
                    })
                }} </p>
            <p class="font-weight-bold">
                {{ $t('pages.password_change.dont_forget_to_remember') }}
            </p>
        </callout>

        <div v-if="modeReset">
            <div class="d-flex flex-column">
                <div class="d-flex justify-content-center">
                    <a href="/login" id="password_lost">{{ $t('pages.password_change.back_to_login') }}</a>
                </div>
            </div>
        </div>
    </page-card>
</template>

<script>
import Callout from "../layout/Callout";
import PageCard from "../layout/PageCard";
import LoadingInjector from "../../mixins/LoadingInjector";

export default {
    name: 'password-change',
    components: {PageCard, Callout},
    mixins: [
        LoadingInjector
    ],
    props: {
        rzaccount: String,
        url_score: String,
        url_passwordchange: String,
        mode: {
            type: String,
            default: 'change'
        },
        regexAccount: {
            type: String,
            default: '.*',
            required: false,
        },
    },
    data: function () {
        return {
            username: '',
            password: '',
            minPasswordLengthCurrent: 3,
            minPasswordLengthNew: 12,
            passwordConfirmation: '',
            passwordCurrent: '',
            passwordsVisible: false,
            changeInProgress: false,
            changeWasSuccessful: false,
            score: {},
            expiration_policy: {
                days: 365,
                seconds: 31536000,
                policy: "default",
                legacyMode: false
            },
            error: {},
            hints: [],
            loading: false,
            notifications: [],
            regex: {
                account: new RegExp(this.regexAccount),
            }
        };
    },
    created() {
        this.username = this.rzaccount;
        this.errorHandler = this.$shared.errorHandler;
    },
    mounted() {
        console.log(`Mounted PasswordChange (${this.mode})`);
        this.$Echo.private(`rz-account.${this.rzaccount}`)
            .listen('BolognaEvent', (event) => {
                switch (event.name) {
                    case 'PasswordChanged': {
                        this.changeInProgress = false;
                        this.changeWasSuccessful = true;
                        break;
                    }
                    case 'PasswordChangeFailure': {
                        this.passwordCurrent = '';
                        this.modalErrorOpen(event.simpleMessage)
                        this.changeInProgress = false;
                        break;
                    }
                }
            });

        let guestId = document.head.querySelector('meta[name="guest-id"]').getAttribute('content');

        if (this.$Bologna.isGuestId(guestId)) {
            this.$Echo.channel(`guest.${guestId}`)
                .listen('BolognaEvent', (event) => {
                    console.log(event);
                    switch (event.name) {
                        case 'PasswordChanged': {
                            this.changeInProgress = false;
                            this.changeWasSuccessful = true;
                            break;
                        }
                        case 'PasswordChangeFailure': {
                            this.passwordCurrent = '';
                            this.modalErrorOpen(event.simpleMessage)
                            this.changeInProgress = false;
                            break;
                        }
                    }
                });
        }

    },
    methods: {
        fetchPolicy: function () {
            this.axios.get(this.$Bologna.secure_url(`/v1/accounting/password-expiration-interval/${this.username}`), {})
                .then(response => {
                    this.expiration_policy = response.data.data;
                });
        },
        changePassword: function () {
            this.changeInProgress = true;
            this.axios.post(this.url_passwordchange, {
                rzaccount: this.username,
                password: this.password,
                password_confirmation: this.passwordConfirmation,
                old_password: this.passwordCurrent,
                mode: this.mode,
            }).then(response => {

            }).catch(err => {
                this.modalErrorOpen(this.$shared.errorHandlerForResponder(err));
                this.changeInProgress = false;
            })
        },
        resetForm: function () {
            this.passwordCurrent = '';
            this.password = '';
            this.passwordConfirmation = '';
            this.score = {};
        }
    },
    computed: {
        isSendable: function () {
            return this.currentPasswordProbablyOkay
                && this.newPasswordOkay
                && this.newPasswordConfirmationOkay
                && this.usernameValid;
        },
        currentPasswordProbablyOkay() {
            return this.passwordCurrent.length
                ? this.passwordCurrent.length >= this.minPasswordLengthCurrent
                : null;
        },
        passwordReused() {
            return this.password === this.passwordCurrent;
        },
        newPasswordEntered() {
            return this.password.length !== 0;
        },
        newPasswordOkay() {
            if (this.password.length) {
                if (!this.newPasswordLongEnough) {
                    return false;
                }
                if (this.assessmentAvailable) {
                    return this.moreThanZeroDays && !this.passwordReused
                }
            }
            return null;
        },
        newPasswordConfirmationOkay() {
            return this.passwordConfirmation.length
                ? this.password === this.passwordConfirmation
                : null;
        },
        passwordFieldType: function () {
            return this.passwordsVisible ? 'text' : 'password';
        },
        modeReset: function () {
            return this.mode === 'reset';
        },
        modeChange: function () {
            return this.mode === 'change';
        },
        usernameValid: function () {
            return this.regex.account.test(this.username);
        },
        passwordsMatch: function () {
            return this.password === this.passwordConfirmation;
        },
        newPasswordLongEnough: function () {
            return this.password.length >= this.minPasswordLengthNew;
        },
        confirmationEntered: function () {
            return this.passwordConfirmation.length > 0;
        },
        displayedScore: function () {
            return this.score.score > 0 ? this.score.score : 0;
        },
        moreThanZeroDays: function () {
            if (!this.score.assessment) {
                return false;
            }
            return this.score.assessment.days > 0;
        },
        assessmentAvailable: function () {
            return typeof(this.score) !== undefined && this.score.assessment;
        },
        hintsAvailable: function () {
            return this.hints.length > 0;
        },
        effectiveValidity: function () {
            return this.score.assessment.days > this.expiration_policy.days ? this.expiration_policy.days : this.score.assessment.days;
        }
    },
    watch: {
        password: function () {
            if (!this.newPasswordLongEnough || this.passwordReused) {
                return;
            }

            this.loading = true;

            this.axios.post(
                this.url_score,
                {
                    password: this.password
                },
                {
                    headers: {
                        'Accept-Language': this.$i18n.locale,
                    },
                    timeout: 8000,
                }
            ).then(response => {
                this.score = response.data.data;
                this.error = response.data.error;
                let reasons = response.data.data['reasons'];
                this.hints = []
                Object.keys(reasons).forEach(
                    key => {
                        if (reasons[key]['hint']) {
                            console.log(reasons[key]['hint'])
                            this.hints.push(this.$purify.sanitize(reasons[key]['hint']));
                        }
                    }
                )
            }).catch(e => {
                console.log(e);
                this.score = {};
            }).finally(_ => {
                this.loading = false;
            });
        },
        username: function () {
            if (this.usernameValid) {
                this.fetchPolicy();
            }

        }
    }
}
</script>
